import { useState, useEffect } from 'react'
import { hsclLogo2, userProfile } from '../../assets/images'
import { Link } from 'react-router-dom'
import {
    IconUsersGroup,
    IconClock,
    IconChartBar,
    IconCreditCard,
    IconLock
} from '@tabler/icons-react'

export default function LockScreen() {
    const [isLoaded, setIsLoaded] = useState(false)

    useEffect(() => {
        setIsLoaded(true)
    }, [])

    return (
        <div className='flex h-svh overflow-hidden bg-white'>
            {/* Left Side: Brand & Features (40%) */}
            <div className='hidden lg:flex lg:w-[40%] flex-col bg-gradient-to-br from-[#011a4d] via-[#012E80] to-[#01245f] p-16 xl:p-24 text-white relative overflow-hidden'>
                {/* Mesh Glow Blobs */}
                <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-white/10 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#012E80]/30 rounded-full blur-[80px]"></div>

                {/* Logo top-left */}
                <div className={`mb-20 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                    <img src={hsclLogo2} alt='HSCL' className='h-12 w-auto object-contain opacity-90' />
                </div>

                <div className='flex-1 flex flex-col justify-center relative z-10'>
                    <h2 className={`text-4xl xl:text-5xl font-bold mb-16 tracking-tight transition-all duration-1000 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        Hi, Welcome back
                    </h2>

                    <div className='space-y-10'>
                        {[
                            { icon: IconUsersGroup, title: 'Comprehensive HR Management', desc: 'Streamline employee data, departments, and attendance tracking in one central hub.' },
                            { icon: IconClock, title: 'Leave & Time Tracking', desc: 'Effortlessly manage leave requests, balances, and detailed timesheets for your workforce.' },
                            { icon: IconChartBar, title: 'Performance & KPI Insights', desc: 'Monitor organizational growth with intuitive KPI dashboards and performance history tracking.' },
                            { icon: IconCreditCard, title: 'Financial & Accounts Control', desc: 'Stay on top of your bottom line with integrated management of payments, expenses, and invoices.' }
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className={`flex items-start gap-6 group transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                                style={{ transitionDelay: `${100 + (idx * 150)}ms` }}
                            >
                                <div className='p-3.5 bg-white/10 rounded-2xl group-hover:bg-white/20 transition-all duration-300 shadow-lg shadow-black/5'>
                                    <item.icon size={26} className='text-white' />
                                </div>
                                <div className='pt-1'>
                                    <h3 className='text-lg xl:text-xl font-bold mb-1.5 group-hover:text-white transition-colors'>{item.title}</h3>
                                    <p className='text-white/70 text-sm leading-relaxed max-w-sm font-light'>
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Side: Lock Form (60%) */}
            <div className='w-full lg:w-[60%] flex flex-col bg-white overflow-y-auto px-8 md:px-16 lg:px-24 py-12'>
                <div className={`flex-1 flex items-center justify-center py-20 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className='w-full max-w-[460px]'>
                        <div className='text-center mb-12'>
                            <div className='relative inline-block mb-8'>
                                <div className='absolute inset-0 bg-[#6CBC50]/10 rounded-full scale-110 blur-md animate-pulse'></div>
                                <img src={userProfile} alt='User profile' className='w-32 h-32 rounded-full border-4 border-white shadow-2xl object-cover relative z-10 mx-auto' />
                                <div className='absolute bottom-1 right-2 w-8 h-8 bg-[#6CBC50] rounded-full border-4 border-white flex items-center justify-center z-20'>
                                    <IconLock size={14} className='text-white' />
                                </div>
                            </div>
                            <h1 className='text-3xl font-black text-gray-900 mb-1 tracking-tight'>Alizee Thomas</h1>
                            <p className="text-gray-500 font-light mb-8">info@example.com</p>
                        </div>

                        <form className='flex flex-col gap-6'>
                            <div className='flex flex-col gap-2'>
                                <label className='text-sm font-semibold text-gray-700 ml-1'>Password</label>
                                <input
                                    type='password'
                                    placeholder='Enter your password to unlock'
                                    className='w-full px-5 py-4 rounded-xl border border-gray-200 focus:border-[#6CBC50] focus:ring-2 focus:ring-[#6CBC50]/20 transition-all outline-none text-gray-800'
                                />
                            </div>

                            <button
                                type='button'
                                className='mt-4 py-4 bg-[#012E80] hover:bg-[#011a4d] text-white text-lg font-bold rounded-xl transition-all duration-300 transform active:scale-[0.98] shadow-lg shadow-[#012E80]/20'
                            >
                                Unlock Now
                            </button>

                            <div className='text-center mt-6'>
                                <p className='text-sm text-gray-500 font-light'>
                                    Not you?{' '}
                                    <Link to="/auth-login" className='font-bold text-[#6CBC50] hover:underline'>
                                        Sign in with a different account
                                    </Link>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Footer Visibility Improved */}
                <div className={`mt-auto pt-10 flex justify-center transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <div className='flex items-center gap-3 opacity-70 hover:opacity-100 transition-all duration-500 py-4 px-8 rounded-full border border-gray-200 hover:border-[#6CBC50]/20 hover:bg-[#6CBC50]/5 group cursor-default'>
                        <span className='text-[10px] uppercase tracking-[0.3em] font-medium text-gray-500'>Powered by</span>
                        <img src={hsclLogo2} alt='HSCL' className='h-6 w-auto object-contain brightness-95 group-hover:brightness-100' />
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.1; transform: scale(1); }
                    50% { opacity: 0.2; transform: scale(1.1); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 8s infinite ease-in-out;
                }
            `}</style>
        </div>
    )
}
